import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Button, Paper, TextField, MenuItem, InputAdornment, Chip, Typography, IconButton, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import TrainingTable from '../components/trainings/TrainingTable';
import TrainingDetailsDrawer from '../components/trainings/TrainingDetailsDrawer';
import AddTrainingDialog from '../components/trainings/AddTrainingDialog';
import AddParticipantDialog from '../components/trainings/AddParticipantDialog';
import apiService from '../services/apiServices';
import normalizeWorker from '../services/workerNormalizer';
import notificationsService from '../services/notificationsService';
import { TRAINING_STATUSES, TRAINING_TYPES, TRAINING_DEPARTMENTS, DEFAULT_FUNDING_TYPE, fundingLabel, trainingStatusLabel, trainingTypeLabel } from '../constants/trainings';
import usePermissions from '../hooks/usePermissions';

const mockTrainings = [
  {
    id: 't1',
    name: 'Onboarding Security',
    type: 'Mandatory',
    description: 'Hyrje në politikat e sigurisë dhe praktikat e TI.',
    trainer: 'Arben K.',
    location: 'Online',
    startDate: '2025-12-05',
    endDate: '2025-12-05',
    status: 'Planned',
    capacity: 20,
    participants: [
      { id: 'p1', name: 'Arian Shehu', department: 'IT', status: 'Not started' },
      { id: 'p2', name: 'Elira Dema', department: 'Finance', status: 'Not started' },
    ],
  },
  {
    id: 't2',
    name: 'Leadership Essentials',
    type: 'Recommended',
    description: 'Program 2-javor për menaxhim ekipesh dhe komunikim.',
    trainer: 'Erisa M.',
    location: 'On-site',
    startDate: '2025-11-20',
    endDate: '2025-12-04',
    status: 'In progress',
    capacity: 15,
    participants: [
      { id: 'p3', name: 'Blerta Hoxha', department: 'HR', status: 'In progress' },
      { id: 'p4', name: 'Gentian Bala', department: 'Sales', status: 'In progress' },
      { id: 'p5', name: 'Arian Shehu', department: 'IT', status: 'Completed', completionDate: '2025-11-27', score: 92 },
    ],
  },
  {
    id: 't3',
    name: 'Advanced Excel për Finance',
    type: 'Recommended',
    description: 'Analiza financiare, pivot, dashboards.',
    trainer: 'Mentor P.',
    location: 'Online',
    startDate: '2025-11-10',
    endDate: '2025-11-12',
    status: 'Completed',
    capacity: 25,
    participants: [
      { id: 'p6', name: 'Elira Dema', department: 'Finance', status: 'Completed', completionDate: '2025-11-12', score: 88 },
      { id: 'p7', name: 'Blerim Krasniqi', department: 'Finance', status: 'Completed', completionDate: '2025-11-12', score: 80 },
    ],
  },
  {
    id: 't4',
    name: 'Sales Playbook 2025',
    type: 'Mandatory',
    description: 'Proceset e reja të shitjes dhe CRM.',
    trainer: 'Nora D.',
    location: 'On-site',
    startDate: '2025-11-01',
    endDate: '2025-11-02',
    status: 'Cancelled',
    capacity: 30,
    participants: [
      { id: 'p8', name: 'Gentian Bala', department: 'Sales', status: 'Not started' },
      { id: 'p9', name: 'Aldo Pasha', department: 'Sales', status: 'Not started' },
    ],
  },
];

const createEmptyTraining = () => {
  const today = new Date().toISOString().slice(0, 10);
  return {
    name: '',
    type: TRAINING_TYPES[0],
    description: '',
    trainer: '',
    location: 'Online',
    startDate: today,
    endDate: today,
    status: TRAINING_STATUSES[0],
    capacity: 0,
    fundingType: DEFAULT_FUNDING_TYPE,
    participants: [],
  };
};

const toDateOnly = (value) => {
  if (!value) return '';
  // If already in YYYY-MM-DD format, return as-is
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const getHashParams = () => {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash || '';
  const queryIndex = hash.indexOf('?');
  if (queryIndex === -1) return null;
  const search = hash.substring(queryIndex + 1);
  return new URLSearchParams(search);
};

const buildInviteLink = (trainingId, participantId) => {
  if (typeof window === 'undefined') return '';
  const base = window.location.origin;
  return `${base}/#/trainings?acceptTraining=${encodeURIComponent(trainingId)}&participant=${encodeURIComponent(
    participantId
  )}`;
};

const uniqueEmails = (list = []) => Array.from(new Set(list.filter(Boolean)));

export default function TrainingsPage() {
  // Lejet: krijimi me `trainings.create`, editimi (edhe financimi) me `trainings.edit`.
  // Serveri i zbaton vetë; këtu vetëm fshihen/çkyçen kontrollet.
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('trainings.create');
  const canEdit = hasPermission('trainings.edit');
  const [editingTraining, setEditingTraining] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: 'All',
    department: 'All',
    type: 'All',
    fundingType: 'All',
  });
  const [selectedId, setSelectedId] = useState(null);
  const [trainings, setTrainings] = useState(mockTrainings);
  const [loading, setLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newTrainingDraft, setNewTrainingDraft] = useState(() => createEmptyTraining());
  const [participantDialogOpen, setParticipantDialogOpen] = useState(false);
  const [participantTargetId, setParticipantTargetId] = useState(null);
  const [inviteSending, setInviteSending] = useState({ trainingId: null, participantId: null });
  const [editingParticipant, setEditingParticipant] = useState(null);

  const persistTraining = useCallback(async (training) => {
    const payload = {
      training: {
        id: training.id,
        name: training.name,
        type: training.type,
        description: training.description,
        trainer: training.trainer,
        location: training.location,
        startDate: toDateOnly(training.startDate || training.start_date),
        endDate: toDateOnly(training.endDate || training.end_date),
        status: training.status,
        capacity: training.capacity,
        fundingType: training.fundingType || DEFAULT_FUNDING_TYPE,
        workingTypeCode: training.workingTypeCode || null,
      },
      participants: (training.participants || []).map((p) => ({
        id: typeof p.id === 'string' && p.id.startsWith('temp-') ? null : p.id,
        name: p.name,
        email: p.email,
        department: p.department,
        status: p.status,
        inviteStatus: p.inviteStatus,
        completionDate: toDateOnly(p.completionDate || p.completion_date),
        score: p.score,
      })),
    };
    const res = await apiService().execute('savetraining', payload, 'post');
    if (res?.success && res.data?.[0]) return res.data[0];
    return null;
  }, []);

  const loadTrainings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService().query('getTrainings', '');
      if (res?.success && Array.isArray(res.data) && res.data.length > 0) {
        const mapped = res.data.map((t) => ({
          id: t.Id ?? t.id,
          name: t.Name ?? t.name ?? '',
          type: t.Type ?? t.type ?? '',
          description: t.Description ?? t.description ?? '',
          trainer: t.Trainer ?? t.trainer ?? '',
          location: t.Location ?? t.location ?? '',
          startDate: t.StartDate ?? t.startDate ?? t.start_date ?? '',
          endDate: t.EndDate ?? t.endDate ?? t.end_date ?? '',
          status: t.Status ?? t.status ?? '',
          capacity: t.Capacity ?? t.capacity ?? 0,
          fundingType: t.FundingType ?? t.fundingType ?? DEFAULT_FUNDING_TYPE,
          workingTypeCode: t.WorkingTypeCode ?? t.workingTypeCode ?? null,
          participants: (t.ParticipantDetails ?? t.participants ?? []).map((p) => ({
            id: p.id,
            name: p.name ?? '',
            email: p.email ?? '',
            department: p.department ?? p.DepartmentName ?? '',
            status: p.status ?? 'Not started',
            inviteStatus: p.inviteStatus ?? '',
            completionDate: p.completionDate ?? p.completion_date ?? '',
            score: p.score ?? null,
          })),
        }));
        setTrainings(mapped);
      } else {
        setTrainings(mockTrainings);
      }
    } catch (_) {
      setTrainings(mockTrainings);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTrainings();
  }, [loadTrainings]);

  const filteredTrainings = useMemo(() => {
    return trainings.filter((t) => {
      const matchesSearch =
        !filters.search ||
        t.name?.toLowerCase().includes(filters.search.toLowerCase()) ||
        (t.description || '').toLowerCase().includes(filters.search.toLowerCase());
      const matchesStatus = filters.status === 'All' || t.status === filters.status;
      const matchesType = filters.type === 'All' || t.type === filters.type;
      const matchesFunding = !filters.fundingType || filters.fundingType === 'All' || (t.fundingType || DEFAULT_FUNDING_TYPE) === filters.fundingType;
      const matchesDept =
        filters.department === 'All' ||
        (t.participants || []).some((p) => p.department === filters.department);
      return matchesSearch && matchesStatus && matchesType && matchesDept && matchesFunding;
    });
  }, [filters, trainings]);

  // Stafi për zgjedhësin e pjesëmarrësve: pa të, dialogu do të kthehej te
  // shtypja me dorë e dikujt që ekziston tashmë te `workers`.
  const [workers, setWorkers] = useState([]);
  /**
   * Departamentet e vërteta të kompanisë.
   *
   * Zgjedhësi i departamentit te pjesëmarrësi lexonte një listë të shkruar me
   * dorë — «Finance, HR, IT, Sales» — që s'ka lidhje me asnjë kompani përveç
   * shembullit. Prandaj departamenti i një pjesëmarrësi nuk përputhej kurrë me
   * atë të dosjes së tij, dhe filtri sipas departamentit nuk kthente asgjë.
   */
  const [deptNames, setDeptNames] = useState([]);
  useEffect(() => {
    Promise.all([
      apiService().get('workers').catch(() => null),
      apiService().get('departments').catch(() => null),
    ]).then(([wRes, dRes]) => {
      const deps = dRes?.data || dRes;
      const depMap = Array.isArray(deps)
        ? Object.fromEntries(deps.map((d) => [String(d.Id ?? d.id), d.Name ?? d.name]))
        : {};
      if (Array.isArray(deps)) setDeptNames(deps.map((d) => d.Name ?? d.name).filter(Boolean));
      const raw = wRes?.data || wRes;
      if (Array.isArray(raw)) {
        setWorkers(raw.map((w, i) => {
          const n = normalizeWorker(w, i);
          // `DepartmentName` vjen bosh nga API-ja; emri zgjidhet nga id-ja.
          return { ...n, DepartmentName: n.DepartmentName || depMap[String(n.Department)] || '' };
        }));
      }
    })
      .catch(() => {});   // pa staf, dialogu mbetet i përdorshëm me çelësin «i jashtëm»
  }, []);

  const summary = useMemo(() => {
    const active = trainings.filter((t) => t.status === 'Planned' || t.status === 'In progress').length;
    // Muaji krahasohej pa vitin: një trajnim i mbyllur në shkurt 2025 numërohej
    // te «Përfunduar këtë muaj» në shkurt 2026. Numri s'ishte i gabuar sa ishte
    // i pakuptimtë — rritej vetvetiu sa herë kthehej i njëjti muaj.
    const now = new Date();
    const sameMonth = (d) => {
      const x = new Date(d);
      return !isNaN(x) && x.getMonth() === now.getMonth() && x.getFullYear() === now.getFullYear();
    };
    const completedThisMonth = trainings.filter(
      (t) => t.status === 'Completed' && sameMonth(t.endDate)
    ).length;
    const participantsTotal = trainings.reduce((acc, t) => acc + (t.participants || []).length, 0);
    const overdue = trainings.filter(
      (t) => t.status !== 'Completed' && new Date(t.endDate) < new Date()
    ).length;
    return { active, completedThisMonth, participantsTotal, overdue };
  }, [trainings]);

  const selectedTraining = trainings.find((t) => t.id === selectedId) || null;

  const handleExportReport = () => {
    const rows = filteredTrainings;
    const cols = ['name', 'type', 'trainer', 'location', 'startDate', 'endDate', 'status', 'capacity'];
    const headers = ['Emri', 'Lloji', 'Trajneri', 'Vendi', 'Fillimi', 'Mbarimi', 'Statusi', 'Kapaciteti', 'Financimi', 'Pjesëmarrës'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [
      headers.join(','),
      // Eksporti shkon te dosja e trajnimeve dhe te auditori; kodet e brendshme
      // ('Mandatory', 'In progress') s'kanë kuptim jashtë bazës së të dhënave.
      ...rows.map((t) =>
        [...cols.map((c) => esc(
          c === 'type'   ? trainingTypeLabel(t.type)
          : c === 'status' ? trainingStatusLabel(t.status)
          : t[c]
        )), esc(fundingLabel(t.fundingType)), esc((t.participants || []).length)].join(',')
      ),
    ];
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trajnime-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleParticipantComplete = async (trainingId, participantId) => {
    let updated = null;
    setTrainings((prev) =>
      prev.map((t) => {
        if (t.id !== trainingId) return t;
        const participants = t.participants.map((p) =>
          p.id === participantId
            ? { ...p, status: 'Completed', completionDate: new Date().toISOString().slice(0, 10) }
            : p
        );
        updated = { ...t, participants };
        return updated;
      })
    );
    if (updated) {
      const saved = await persistTraining(updated);
      if (saved) {
        setTrainings((prev) => prev.map((t) => (t.id === trainingId ? saved : t)));
      }
    }
  };

  const handleNotify = (trainingId, participantId) => {
    // Placeholder for future API call
    // eslint-disable-next-line no-console
    console.log('Notify participant', { trainingId, participantId });
  };

  const markInviteAccepted = useCallback(
    async (trainingId, participantId) => {
      let updated = null;
      setTrainings((prev) =>
        prev.map((t) => {
          if (`${t.id}` !== `${trainingId}`) return t;
          const nextStatus =
            t.status === 'Planned' || t.status === 'Not started' ? 'In progress' : t.status;
          const participants = (t.participants || []).map((p) =>
            `${p.id}` === `${participantId}`
              ? {
                  ...p,
                  inviteStatus: 'Accepted',
                  status: p.status === 'Not started' ? 'In progress' : p.status,
                }
              : p
          );
          updated = { ...t, status: nextStatus, participants };
          return updated;
        })
      );
      if (updated) {
        const saved = await persistTraining(updated);
        if (saved) {
          setTrainings((prev) => prev.map((t) => (`${t.id}` === `${trainingId}` ? saved : t)));
        }
      }
    },
    [persistTraining]
  );

  useEffect(() => {
    const params = getHashParams();
    const trainingId = params?.get('acceptTraining');
    const participantId = params?.get('participant');
    if (trainingId && participantId) {
      const exists = trainings.some((t) => `${t.id}` === `${trainingId}`);
      if (exists) {
        markInviteAccepted(trainingId, participantId);
        if (typeof window !== 'undefined') {
          window.history.replaceState(null, '', `${window.location.pathname}#/trainings`);
        }
      }
    }
  }, [markInviteAccepted, trainings]);

  const handleSendInvite = async (trainingId, participantId, trainingSnapshot) => {
    const training = trainingSnapshot || trainings.find((t) => `${t.id}` === `${trainingId}`);
    const participant =
      training?.participants?.find((p) => `${p.id}` === `${participantId}`) || null;
    if (!training || !participant) return;
    if (!participant.email) {
      // eslint-disable-next-line no-alert
      alert('Shto një email për pjesëmarrësin për të dërguar ftesën.');
      return;
    }

    const departmentRecipients = uniqueEmails(
      (training.participants || [])
        .filter((p) => p.department === participant.department)
        .map((p) => p.email)
    );
    const recipients = uniqueEmails([participant.email, ...departmentRecipients]);

    const acceptLink = buildInviteLink(trainingId, participantId);
    const subject = `Ftesë për trajnimin: ${training.name}`;
    const html = `
      <div style="font-family: 'Inter', Arial, sans-serif; max-width: 560px; margin: 0 auto; padding: 24px; background: #101828; color: #D0D5DD; border-radius: 16px;">
        <div style="text-align: center; margin-bottom: 16px;">
          <div style="display: inline-flex; align-items: center; gap: 8px; font-weight: 700; font-size: 18px; letter-spacing: 0.5px;">T3 HR • Ftesë trajnimi</div>
        </div>
        <div style="background: #0B1221; border: 1px solid #344054; border-radius: 14px; padding: 18px; margin-bottom: 14px;">
          <p style="margin: 0 0 6px 0; color: #98A2B3;">Përshëndetje ${participant.name},</p>
          <p style="margin: 0; font-size: 16px;">Je ftuar të marrësh pjesë në <strong style="color: #C7D2FE;">${training.name}</strong>.</p>
        </div>
        <div style="background: #0B1221; border: 1px solid #344054; border-radius: 14px; padding: 16px; color: #D0D5DD;">
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; font-size: 14px;">
            <div><div style="color:#667085; text-transform: uppercase; font-size:11px;">Lokacioni</div><div style="font-weight:600;">${training.location || '—'}</div></div>
            <div><div style="color:#667085; text-transform: uppercase; font-size:11px;">Data</div><div style="font-weight:600;">${training.startDate || ''}${
      training.endDate && training.endDate !== training.startDate ? ` - ${training.endDate}` : ''
    }</div></div>
            <div><div style="color:#667085; text-transform: uppercase; font-size:11px;">Statusi</div><div style="font-weight:600;">${trainingStatusLabel(training.status)}</div></div>
            <div><div style="color:#667085; text-transform: uppercase; font-size:11px;">Trajneri</div><div style="font-weight:600;">${training.trainer || '—'}</div></div>
          </div>
        </div>
        <div style="text-align: center; margin: 18px 0;">
          <a href="${acceptLink}" style="display: inline-block; background: #3E8FA3; color: #0B1221; padding: 12px 18px; border-radius: 999px; font-weight: 700; text-decoration: none; box-shadow: 0 10px 30px rgba(99,102,241,0.35);">
            Prano ftesën
          </a>
        </div>
        <p style="color:#98A2B3; font-size: 13px; line-height: 1.5; text-align:center;">
          Nëse lidhja nuk hapet, kopjo këtë adresë: <br/>
          <span style="color:#C7D2FE;">${acceptLink}</span>
        </p>
      </div>
    `;
    const text = `Pershendetje ${participant.name},

Je ftuar në trajnimin "${training.name}".
Lokacioni: ${training.location || '—'} | Data: ${training.startDate || ''}${
      training.endDate && training.endDate !== training.startDate ? ` - ${training.endDate}` : ''
    }

Prano ftesën këtu: ${acceptLink}
`;

    setInviteSending({ trainingId, participantId });
    let updated = null;
    setTrainings((prev) =>
      prev.map((t) => {
        if (`${t.id}` !== `${trainingId}`) return t;
        const participants = (t.participants || []).map((p) =>
          `${p.id}` === `${participantId}` ? { ...p, inviteStatus: 'Sent' } : p
        );
        updated = { ...t, participants };
        return updated;
      })
    );

    try {
      await notificationsService.sendEmail({
        to: recipients,
        subject,
        html,
        text,
      });
      if (updated) {
        const saved = await persistTraining(updated);
        if (saved) {
          setTrainings((prev) => prev.map((t) => (`${t.id}` === `${trainingId}` ? saved : t)));
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Nuk u dërgua ftesa', error);
      // eslint-disable-next-line no-alert
      alert('Nuk u dërgua email-i. Kontrollo konfigurimin e email-it.');
      if (trainingSnapshot) {
        setTrainings((prev) =>
          prev.map((t) => (`${t.id}` === `${trainingId}` ? trainingSnapshot : t))
        );
      }
    } finally {
      setInviteSending({ trainingId: null, participantId: null });
    }
  };

  const handleAddTraining = async (draft) => {
    setAddDialogOpen(false);
    // Editim: i njëjti dialog, me id — serveri kërkon `trainings.edit`.
    if (editingTraining) {
      const payload = { ...editingTraining, ...draft, id: editingTraining.id };
      setEditingTraining(null);
      const saved = await persistTraining(payload);
      if (saved) setTrainings((prev) => prev.map((t) => (`${t.id}` === `${saved.id}` ? { ...t, ...saved } : t)));
      return;
    }
    const payload = { ...draft, participants: [] };
    const saved = await persistTraining(payload);
    if (saved) setTrainings((prev) => [saved, ...prev]);
    else setTrainings((prev) => [{ ...payload, id: `local-${Date.now()}` }, ...prev]);
  };
  const openEditDialog = (training) => {
    setEditingTraining(training);
    setNewTrainingDraft({
      name: training.name || '', description: training.description || '', type: training.type || TRAINING_TYPES[0],
      status: training.status || TRAINING_STATUSES[0], startDate: toDateOnly(training.startDate) || '', endDate: toDateOnly(training.endDate) || '',
      location: training.location || '', trainer: training.trainer || '', capacity: training.capacity ?? '',
      fundingType: training.fundingType || DEFAULT_FUNDING_TYPE,
      workingTypeCode: training.workingTypeCode || 'TRAINING_INTERNAL',
    });
    setAddDialogOpen(true);
  };

  const openAddDialog = () => {
    setNewTrainingDraft(createEmptyTraining());
    setAddDialogOpen(true);
  };

  const handleOpenAddParticipant = (trainingId) => {
    setParticipantTargetId(trainingId);
    setEditingParticipant(null);
    setParticipantDialogOpen(true);
  };

  const handleOpenEditParticipant = (trainingId, participant) => {
    setParticipantTargetId(trainingId);
    setEditingParticipant(participant);
    setParticipantDialogOpen(true);
  };

  const handleAddParticipant = async (participant) => {
    if (!participantTargetId) return;
    const participantWithId = {
      ...participant,
      id: participant.id || editingParticipant?.id || `temp-${Date.now()}`,
    };
    let draft = null;
    setTrainings((prev) =>
      prev.map((t) => {
        if (t.id !== participantTargetId) return t;
        const participants = editingParticipant
          ? (t.participants || []).map((p) =>
              `${p.id}` === `${participantWithId.id}` ? { ...p, ...participantWithId } : p
            )
          : [...(t.participants || []), participantWithId];
        const updated = { ...t, participants };
        draft = updated;
        return updated;
      })
    );
    if (draft) {
      const saved = await persistTraining(draft);
      if (saved) {
        setTrainings((prev) => prev.map((t) => (t.id === participantTargetId ? saved : t)));
        const targetId =
          saved.participants?.find(
            (p) => `${p.id}` === `${participantWithId.id}` || p.email === participant.email
          )?.id || null;
        if (!editingParticipant && targetId) {
          handleSendInvite(saved.id, targetId, saved);
        }
      }
    }
    setParticipantDialogOpen(false);
    setParticipantTargetId(null);
    setEditingParticipant(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

      {/* ── Header card: stats + toolbar + filters ── */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>

        {/* Top bar: title + stat chips + action buttons */}
        <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <FilterListIcon sx={{ fontSize: 17, color: 'text.disabled', flexShrink: 0 }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ mr: 0.5 }}>Trajnimet</Typography>
          {[
            { label: `${summary.active} Aktive`,           color: '#256878', bg: '#ECFEFF'  },
            { label: `${summary.completedThisMonth} Përfunduar`, color: '#15803D', bg: '#F0FDF4' },
            { label: `${summary.participantsTotal} Pjesëmarrës`, color: '#0891B2', bg: '#F0F9FF' },
            { label: `${summary.overdue} Vonuara`,         color: '#B45309', bg: '#FFFBEB'  },
          ].map(s => (
            <Chip key={s.label} size="small" label={s.label}
              sx={{ bgcolor: s.bg, color: s.color, fontWeight: 700, fontSize: '0.7rem', height: 22, border: 'none' }} />
          ))}
          <Box sx={{ flex: 1 }} />
          <Stack direction="row" spacing={0.75} alignItems="center" flexShrink={0}>
            {canCreate && <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openAddDialog}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, fontSize: '0.78rem', py: 0.55 }}>
              Shto trajnim
            </Button>}
            <Tooltip title="Eksporto raport">
              <Button variant="outlined" size="small" color="inherit" onClick={handleExportReport}
                sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.55, display: { xs: 'none', sm: 'inline-flex' } }}>
                Eksporto
              </Button>
            </Tooltip>
          </Stack>
        </Box>

        {/* Filter row */}
        <Box sx={{ px: 2, py: 1, bgcolor: '#F8FAFC', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small" placeholder="Kërko trajnimin…"
            value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 14, color: 'text.disabled' }} /></InputAdornment> }}
            sx={{ width: 200, '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.55 }, '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff' } }}
          />
          <TextField select size="small" value={filters.status}
            onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
            SelectProps={{ renderValue: v => v === 'All' ? 'Statusi' : v }}
            sx={{ width: 130, '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.6 }, '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff' } }}>
            {['All', ...TRAINING_STATUSES].map(s => (
              <MenuItem key={s} value={s} sx={{ fontSize: '0.8rem' }}>{s === 'All' ? 'Të gjithë' : s}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" value={filters.department}
            onChange={e => setFilters(f => ({ ...f, department: e.target.value }))}
            SelectProps={{ renderValue: v => v === 'All' ? 'Departamenti' : v }}
            sx={{ width: 140, '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.6 }, '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff' } }}>
            {['All', ...TRAINING_DEPARTMENTS].map(d => (
              <MenuItem key={d} value={d} sx={{ fontSize: '0.8rem' }}>{d === 'All' ? 'Të gjithë' : d}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" value={filters.type}
            onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}
            SelectProps={{ renderValue: v => v === 'All' ? 'Lloji' : v }}
            sx={{ width: 120, '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.6 }, '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff' } }}>
            {['All', ...TRAINING_TYPES].map(t => (
              <MenuItem key={t} value={t} sx={{ fontSize: '0.8rem' }}>{t === 'All' ? 'Të gjithë' : t}</MenuItem>
            ))}
          </TextField>
          {(filters.search || filters.status !== 'All' || filters.department !== 'All' || filters.type !== 'All') && (
            <Chip size="small" label="Pastro"
              onDelete={() => setFilters({ search: '', status: 'All', department: 'All', type: 'All' })}
              sx={{ fontSize: '0.7rem', height: 22 }} />
          )}
          <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto', whiteSpace: 'nowrap' }}>
            {filteredTrainings.length} trajnime
          </Typography>
        </Box>
      </Paper>

      <TrainingTable trainings={filteredTrainings} onView={(id) => setSelectedId(id)} />

      <TrainingDetailsDrawer
        open={Boolean(selectedTraining)}
        training={selectedTraining}
        onClose={() => setSelectedId(null)}
        onParticipantComplete={handleParticipantComplete}
        onNotify={handleNotify}
        onAddParticipant={handleOpenAddParticipant}
        onEditParticipant={handleOpenEditParticipant}
        onSendInvite={handleSendInvite}
        inviteSending={inviteSending}
        onAcceptInvite={markInviteAccepted}
        onEditTraining={canEdit ? openEditDialog : null}
      />

      <AddTrainingDialog
        open={addDialogOpen}
        onClose={() => { setAddDialogOpen(false); setEditingTraining(null); }}
        onSubmit={handleAddTraining}
        initialValues={newTrainingDraft}
        mode={editingTraining ? 'edit' : 'create'}
        canEditFunding={editingTraining ? canEdit : (canCreate || canEdit)}
      />

      <AddParticipantDialog
        open={participantDialogOpen}
        onClose={() => {
          setParticipantDialogOpen(false);
          setParticipantTargetId(null);
          setEditingParticipant(null);
        }}
        onSubmit={handleAddParticipant}
        workers={workers}
        departments={deptNames}
        initialValues={editingParticipant || undefined}
        title={editingParticipant ? 'Përditëso pjesëmarrësin' : 'Shto pjesëmarrës'}
        submitLabel={editingParticipant ? 'Ruaj' : 'Shto'}
      />
    </Box>
  );
}
